import { ArrowUturnLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { Button } from "@fronterahealth/frontera-ui-components/ui/button";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { CommentActionTooltip } from "@components/QaReview/components/CommentActionTooltip";
import { useCanReply } from "@components/QaReview/hooks/useCanReply";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";

interface CommentActionReplyProps {
  comment: ReviewItemFieldsFragment;
  /** Icon only, for a reply's header row; the label moves to aria-label. */
  iconOnly?: boolean;
}

export const CommentActionReply: React.FC<CommentActionReplyProps> = ({ comment, iconOnly = false }) => {
  const canReply = useCanReply(comment);
  const { state, dispatch } = useReviewSession();
  const threadRootId = comment.parent?.id ?? comment.id;
  const replying = state.composer?.kind === "reply" && state.composer.replyToId === comment.id;

  if (!canReply) return null;

  const toggleReply = () => {
    if (replying) {
      dispatch({ type: "closeComposer" });
      return;
    }
    dispatch({ type: "openComposer", composer: { kind: "reply", parentId: threadRootId, replyToId: comment.id } });
    dispatch({ type: "expandReplies", rootId: threadRootId });
  };

  const label = replying ? "Cancel" : "Reply";

  return (
    <CommentActionTooltip label={replying ? "Close the reply box without posting" : "Write a reply in this thread"}>
      <Button
        variant="ghost"
        size={iconOnly ? "icon-xs" : "xs"}
        aria-label={iconOnly ? label : undefined}
        onClick={(e) => {
          e.stopPropagation();
          toggleReply();
        }}
      >
        {/* With no text, the icon is what shows the button now closes the box (and drops the draft). */}
        {iconOnly && replying ? <XMarkIcon className="h-3.5 w-3.5" /> : <ArrowUturnLeftIcon className="h-3.5 w-3.5" />}
        {!iconOnly && label}
      </Button>
    </CommentActionTooltip>
  );
};
